/**
 * What has been sent to the register, and what became of it.
 *
 * One row per send, and a dialog behind each one listing the people it went to
 * with the state of their own copy. The two levels are the point: a send is
 * "forty letters went out on the second", and only the recipient rows can say
 * that two of the forty bounced and one address has since told us to stop.
 *
 * ## Both kinds of send are here
 *
 * The payment requests `PaymentRequestModal` sends and the letters
 * `ShareholderMailModal` sends write the same two records, so they list
 * together. A register's owner asking "what have we sent these people" does not
 * mean "what have we sent them that was not about money".
 *
 * ## A draft is in the same list
 *
 * It has no recipients, no figures and no send date, so its row shows when it
 * was last saved and opens the compose dialog rather than a list of people it
 * never reached. Keeping it here rather than in a drawer of its own is the
 * point: "what have we written to these people" is one question, and half an
 * answer in two places is how a half-written letter gets forgotten.
 *
 * ## Delivery is counted from the recipients, not from the send
 *
 * `sent` and `failed` on the send document say what Resend accepted from us.
 * Whether a message arrived is a fact that turns up minutes later on the
 * recipient's own row, by webhook — so the Delivered column counts rows, and a
 * send whose figures have not moved yet reads as delivered-to-nobody rather
 * than claiming an outcome nobody has heard.
 */
import { ResendNonOpeners, type ResendCounts } from '@/features/newsletter/ResendNonOpeners'
import { callFunction } from '@/shared/firebase/callFunction'
import { useMemo, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { formatDate } from '@/shared/util/dates'
import {
  HOLDER_STATUSES,
  isDraft,
  MAIL_STATES,
  type ShareholderMail,
  type ShareholderSend,
} from '@/types/ownership'
import { deleteShareholderDraft } from './repository/ownershipRepository'
import './Mail.css'

const nb = (n: number) => n.toLocaleString('nb-NO')

const SEND_STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'mail__pill--muted' },
  sending: { label: 'Sending', cls: 'mail__pill--busy' },
  finished: { label: 'Sent', cls: 'mail__pill--ok' },
  'finished-with-errors': { label: 'Sent, with errors', cls: 'mail__pill--warn' },
}

/** What the audience says, in the words the compose screen used. */
function audienceOf(s: ShareholderSend): string {
  if (s.audience === 'issuance-subscribers') {
    return s.issuanceName ? `Subscribers — ${s.issuanceName}` : 'Issuance subscribers'
  }
  const names = s.groups.map(
    (g) => HOLDER_STATUSES.find((h) => h.key === g)?.label ?? g,
  )
  const who = names.length ? names.join(', ') : 'The register'
  return s.issuanceName ? `${who} — ${s.issuanceName}` : who
}

export function MailLog({
  sends,
  mail,
  onCompose,
  onOpenDraft,
}: {
  sends: ShareholderSend[]
  mail: ShareholderMail[]
  onCompose: () => void
  /** Reopen a draft in the compose dialog. */
  onOpenDraft: (draft: ShareholderSend) => void
}) {
  const [open, setOpen] = useState<ShareholderSend | null>(null)

  /** Recipient rows by send, so a row can count its own deliveries. */
  const bySend = useMemo(() => {
    const m = new Map<string, ShareholderMail[]>()
    for (const x of mail) {
      if (!x.sendId) continue
      m.set(x.sendId, [...(m.get(x.sendId) ?? []), x])
    }
    return m
  }, [mail])

  return (
    <div className="mail">
      <div className="mail__head">
        <div>
          <h2 className="mail__title">Email</h2>
          <p className="mail__lede">
            Everything this register has sent, and what happened to each copy.
          </p>
        </div>
        <button className="btn btn--primary" onClick={onCompose}>
          New email
        </button>
      </div>

      {sends.length === 0 ? (
        <p className="empty-note">Nothing has been sent from the register yet.</p>
      ) : (
        <div className="mail__table" role="table" aria-label="Sent email">
          <div className="mail__row mail__row--head" role="row">
            <span role="columnheader">Sent</span>
            <span role="columnheader">Subject</span>
            <span role="columnheader">Recipients</span>
            <span role="columnheader">Delivered</span>
            <span role="columnheader">Problems</span>
            <span role="columnheader">Status</span>
          </div>
          {sends.map((s) => {
            const rows = bySend.get(s.id) ?? []
            const delivered = rows.filter((r) => r.status === 'delivered').length
            const bad = rows.filter(
              (r) => r.status === 'bounced' || r.status === 'complained',
            ).length
            const state = SEND_STATUS[s.status] ?? SEND_STATUS.finished!
            const draft = isDraft(s)
            return (
              <button
                className="mail__row mail__row--click"
                role="row"
                key={s.id}
                onClick={() => (draft ? onOpenDraft(s) : setOpen(s))}
              >
                <span role="cell" className="mail__when">
                  {/* A draft has never started, so the date it shows is the day
                      somebody last worked on it. */}
                  {draft
                    ? s.updatedAt
                      ? `Saved ${formatDate(s.updatedAt.slice(0, 10))}`
                      : 'Not sent'
                    : s.startedAt
                      ? formatDate(s.startedAt.slice(0, 10))
                      : '—'}
                  <span className="micro">{s.startedBy}</span>
                </span>
                <span role="cell" className="mail__subject">
                  {s.subject}
                  <span className="micro">
                    {audienceOf(s)}
                    {s.attachmentCount > 0 &&
                      ` · ${s.attachmentCount} attachment${s.attachmentCount === 1 ? '' : 's'}`}
                  </span>
                </span>
                <span role="cell">{draft ? '—' : nb(s.sent || s.total)}</span>
                <span role="cell">{!draft && rows.length ? nb(delivered) : '—'}</span>
                <span role="cell" className={bad > 0 ? 'mail__bad' : undefined}>
                  {bad > 0 ? nb(bad) : '—'}
                </span>
                <span role="cell" className="mail__last">
                  <span className={`mail__pill ${state.cls}`}>{state.label}</span>
                  {draft && (
                    <span
                      className="mail__discard"
                      role="button"
                      tabIndex={0}
                      title="Discard this draft"
                      // Stops the row opening the draft it is about to delete.
                      onClick={(e) => {
                        e.stopPropagation()
                        if (window.confirm('Discard this draft?')) {
                          void deleteShareholderDraft(s.id)
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return
                        e.stopPropagation()
                        e.preventDefault()
                        if (window.confirm('Discard this draft?')) {
                          void deleteShareholderDraft(s.id)
                        }
                      }}
                    >
                      Discard
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {open && (
        <RecipientsModal
          send={open}
          rows={(bySend.get(open.id) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'nb'))}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}

/**
 * Who this send went to, one row each.
 *
 * `openedAt` is a ceiling and is labelled as one: Apple Mail Privacy Protection
 * fetches the tracking pixel whether or not a person looked at the message, so
 * this column means *fetched*, not *read*. Saying "Read" over it would be a
 * claim the data cannot support about a named investor.
 */
function RecipientsModal({
  send,
  rows,
  onClose,
}: {
  send: ShareholderSend
  rows: ShareholderMail[]
  onClose: () => void
}) {
  const delivered = rows.filter((r) => r.status === 'delivered').length
  const opened = rows.filter((r) => r.openedAt).length

  return (
    <Modal
      title={send.subject || 'Send'}
      subtitle={`${audienceOf(send)} · ${send.startedAt ? formatDate(send.startedAt.slice(0, 10)) : ''}`}
      onClose={onClose}
      width={780}
      footer={
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="mail__detail">
        <div className="mail__stats">
          <span>
            <strong>{nb(rows.length || send.sent)}</strong> sent
          </span>
          <span>
            <strong>{nb(delivered)}</strong> delivered
          </span>
          <span>
            <strong>{nb(opened)}</strong> opened
          </span>
        </div>

        {rows.length === 0 ? (
          <p className="empty-note">
            This send has no per-recipient rows. Sends made before the register kept them show only
            their totals.
          </p>
        ) : (
          <div className="mail__table mail__table--detail" role="table" aria-label="Recipients">
            <div className="mail__row mail__row--head mail__row--detail" role="row">
              <span role="columnheader">Name</span>
              <span role="columnheader">Address</span>
              <span role="columnheader">Shares</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Opened</span>
            </div>
            {rows.map((r) => {
              const state = MAIL_STATES[r.status]
              return (
                <div className="mail__row mail__row--detail" role="row" key={r.id}>
                  <span role="cell">{r.name || '—'}</span>
                  <span role="cell" className="mail__addr">
                    {r.email}
                  </span>
                  <span role="cell">{nb(r.shares)}</span>
                  <span role="cell">
                    <span className={`mail__pill mail__pill--${state.tone}`}>{state.label}</span>
                    {r.failure && <span className="micro"> {r.failure}</span>}
                  </span>
                  <span role="cell">
                    {r.openedAt ? (
                      <>
                        {formatDate(r.openedAt.slice(0, 10))}
                        {(r.opens ?? 0) > 1 && <span className="micro"> ×{r.opens}</span>}
                      </>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        <p className="micro mail__caveat">
          Opened means a mail client fetched the message, which some do without anybody reading it.
          It is a ceiling, not a count of readers.
        </p>

        {send.audience === 'shareholders' && (
          <ResendNonOpeners
            state={{
              status: send.status,
              finishedAt: send.finishedAt ? new Date(send.finishedAt) : null,
              subject: send.subject,
              resendOf: send.resendOf || null,
              resentAs: send.resentAs || null,
              resentAt: send.resentAt ? new Date(send.resentAt) : null,
              blocked:
                send.attachmentCount > 0
                  ? 'This letter carried files, which are not kept — send it again from the register with them attached.'
                  : undefined,
            }}
            count={() =>
              callFunction<ResendCounts>('crmSendShareholderMail', {
                method: 'POST',
                body: { mode: 'resend-count', sendId: send.id },
              })
            }
            send={(subject) =>
              callFunction<{ sent: number }>('crmSendShareholderMail', {
                method: 'POST',
                body: { mode: 'resend', sendId: send.id, subject },
              }).then((r) => r.sent)
            }
          />
        )}
      </div>
    </Modal>
  )
}
